import React, {useEffect, useState} from "react";
import {Button, Descriptions, Drawer, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tabs, Tag, Tooltip} from "antd";
import {message} from "../../utils/antdFeedback";
import {KeyOutlined, PlusOutlined, PrinterOutlined, ShopOutlined} from "@ant-design/icons";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {adminRequest, buildQuery, getReturnedObject} from "../../api/adminApi";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/store";
import {Card, COLORS, HeroHeader, SearchField, formatNaira} from "../../designSystem";

const pageInfo = (items: any[] = []) => ({
  pageNumber: 1,
  pageSize: items.length || 10,
  totalItems: items.length,
  totalPages: 1,
  totalRecords: items.length,
});
const money = (value: any, currency = "NGN") => currency === "NGN" ? formatNaira(value) : `${currency} ${Number(value || 0).toLocaleString()}`;

// The API serialises enums as integers, but some payloads stringify them, so both are accepted.
const MERCHANT_STATUS: Record<number, string> = {1: "Pending", 2: "Active", 3: "Suspended"};
const statusText = (value: any) =>
  typeof value === "string" && !/^\d+$/.test(value) ? value : MERCHANT_STATUS[Number(value)] || String(value ?? "");

/** Basis points as a percentage: 150 is 1.5%. */
const bps = (value: any) => `${((Number(value) || 0) / 100).toFixed(2)}%`;

function Merchants() {
  const {token} = useSelector((state: RootState) => state.account);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [links, setLinks] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [disputes, setDisputes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState<any[]>([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [topLevelOnly, setTopLevelOnly] = useState(true);
  // Sub-merchants kept per platform id, so an expanded row keeps what it loaded.
  const [subMerchants, setSubMerchants] = useState<Record<string, any[]>>({});
  const [subMerchantsLoadingFor, setSubMerchantsLoadingFor] = useState<string | null>(null);
  const [feeOpen, setFeeOpen] = useState(false);
  const [feeForm] = Form.useForm();
  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [keyOpen, setKeyOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | undefined>();
  const [form] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [keyForm] = Form.useForm();
  const [userForm] = Form.useForm();

  const loadMerchants = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await adminRequest(`/merchants${buildQuery({
        searchParam,
        // Flattening is the only way a search can match a sub-merchant, so the roots-only
        // filter steps aside whenever there is a search term.
        topLevelOnly: topLevelOnly && !searchParam ? true : undefined,
      })}`, {token});
      const data = getReturnedObject(response);
      setMerchants((data?.items || data?.Items || []).map((item: any) => ({...item, key: item.id})));
    } catch (error: any) {
      message.error(error.message || "Could not load merchants");
    } finally {
      setLoading(false);
    }
  };

  const loadSubMerchants = async (merchantId: string) => {
    if (!token || subMerchants[merchantId]) return;
    setSubMerchantsLoadingFor(merchantId);
    try {
      const response = await adminRequest(`/merchants/${merchantId}/sub-merchants`, {token});
      const data = getReturnedObject(response);
      setSubMerchants((current) => ({
        ...current,
        [merchantId]: (Array.isArray(data) ? data : data?.items || []).map((item: any) => ({...item, key: item.id})),
      }));
    } catch (error: any) {
      message.error(error.message || "Could not load sub-merchants");
    } finally {
      setSubMerchantsLoadingFor(null);
    }
  };

  const setPlatformFee = async (values: any) => {
    if (!token || !selected?.id) return;
    try {
      const updated = await adminRequest(`/merchants/${selected.id}/platform-fee`, {
        method: "PUT",
        token,
        body: JSON.stringify({
          flatAmount: Number(values.flatAmount) || 0,
          percentageBps: Number(values.percentageBps) || 0,
          cap: values.cap ?? null,
          waiveFlatBelow: values.waiveFlatBelow ?? null,
        }),
      });
      message.success("Platform fee updated");
      setSelected(getReturnedObject(updated) || selected);
      setFeeOpen(false);
      loadMerchants();
    } catch (error: any) {
      message.error(error.message || "Could not update platform fee");
    }
  };

  const subMerchantColumns = [
    {title: "Sub-merchant", dataIndex: "businessName", key: "businessName"},
    {title: "Email", dataIndex: "businessEmail", key: "businessEmail"},
    {title: "Status", dataIndex: "status", key: "status", render: (value: any) => <Tag>{statusText(value)}</Tag>},
    {title: "Their sub-merchants", dataIndex: "subMerchantCount", key: "subMerchantCount", render: (value: any) => value || 0},
    {title: "Users", dataIndex: "userCount", key: "userCount"},
    {title: "Links", dataIndex: "paymentLinkCount", key: "paymentLinkCount"},
    {
      title: "Platform reference",
      dataIndex: "externalReference",
      key: "externalReference",
      render: (value: string) => value || "N/A",
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, row: any) => <Button size="small" onClick={() => loadMerchantDetails(row)}>Open</Button>,
    },
  ];

  const loadMerchantDetails = async (merchant: any) => {
    if (!token || !merchant?.id) return;
    setSelected(merchant);
    const [usersRes, keysRes, linksRes, txRes, disputesRes] = await Promise.all([
      adminRequest(`/merchants/${merchant.id}/users`, {token}),
      adminRequest(`/merchants/${merchant.id}/api-keys`, {token}),
      adminRequest(`/merchants/${merchant.id}/payment-links`, {token}),
      adminRequest(`/merchants/${merchant.id}/transactions`, {token}),
      adminRequest(`/merchants/${merchant.id}/disputes`, {token}),
    ]);
    setUsers((getReturnedObject(usersRes) || []).map((item: any) => ({...item, key: item.id})));
    setApiKeys((getReturnedObject(keysRes) || []).map((item: any) => ({...item, key: item.id})));
    setLinks((getReturnedObject(linksRes) || []).map((item: any) => ({...item, key: item.id})));
    const tx = getReturnedObject(txRes);
    setTransactions((tx?.items || tx?.Items || []).map((item: any) => ({...item, key: item.id})));
    const disputeData = getReturnedObject(disputesRes);
    setDisputes((disputeData?.items || disputeData?.Items || []).map((item: any) => ({...item, key: item.id})));
    if ((merchant.subMerchantCount || 0) > 0) loadSubMerchants(merchant.id);
  };

  const loadBanks = async () => {
    if (!token || banks.length > 0) return;
    setBanksLoading(true);
    try {
      const response = await adminRequest("/payment/vfd-banks", {token});
      const data = getReturnedObject(response);
      const rawBanks = data?.banks || data?.Banks || data?.items || data?.Items || data || [];
      setBanks((Array.isArray(rawBanks) ? rawBanks : []).map((bank: any) => ({
        code: bank.code || bank.Code,
        name: bank.name || bank.Name,
      })).filter((bank: any) => bank.code && bank.name));
    } catch (error: any) {
      message.error(error.message || "Could not load settlement banks");
    } finally {
      setBanksLoading(false);
    }
  };

  useEffect(() => {
    loadMerchants();
  }, [token, searchParam, topLevelOnly]);

  useEffect(() => {
    if (createOpen) loadBanks();
  }, [createOpen, token]);

  const settlementBankOptions = banks.map((bank) => ({
    label: `${bank.name} (${bank.code})`,
    value: bank.code,
    bankName: bank.name,
  }));

  const selectSettlementBank = (bankCode: string, option: any) => {
    form.setFieldsValue({
      settlementBankCode: bankCode,
      settlementBankName: option?.bankName || "",
    });
  };

  const createMerchant = async (values: any) => {
    if (!token) return;
    try {
      await adminRequest("/merchants", {method: "POST", token, body: JSON.stringify(values)});
      message.success("Merchant profiled");
      setCreateOpen(false);
      form.resetFields();
      loadMerchants();
    } catch (error: any) {
      message.error(error.message || "Could not profile merchant");
    }
  };

  const createPaymentLink = async (values: any) => {
    if (!token || !selected?.id) return;
    try {
      await adminRequest(`/merchants/${selected.id}/payment-links`, {
        method: "POST",
        token,
        body: JSON.stringify({...values, type: values.type || 1, currency: "NGN"}),
      });
      message.success("Payment link created");
      setLinkOpen(false);
      linkForm.resetFields();
      loadMerchantDetails(selected);
    } catch (error: any) {
      message.error(error.message || "Could not create payment link");
    }
  };

  const createApiKey = async (values: any) => {
    if (!token || !selected?.id) return;
    try {
      const response = await adminRequest(`/merchants/${selected.id}/api-keys`, {
        method: "POST",
        token,
        body: JSON.stringify(values),
      });
      const data = getReturnedObject(response);
      setCreatedKey(data?.plainTextKey);
      message.success("API key generated");
      keyForm.resetFields();
      loadMerchantDetails(selected);
    } catch (error: any) {
      message.error(error.message || "Could not create API key");
    }
  };

  const updateEnvironment = async (environment: number) => {
    if (!token || !selected?.id) return;
    try {
      await adminRequest(`/merchants/${selected.id}`, {
        method: "PUT",
        token,
        body: JSON.stringify({environment}),
      });
      message.success("Environment updated");
      const next = {...selected, currentEnvironment: environment};
      setSelected(next);
      loadMerchants();
    } catch (error: any) {
      message.error(error.message || "Could not update environment");
    }
  };

  const createUser = async (values: any) => {
    if (!token || !selected?.id) return;
    try {
      await adminRequest(`/merchants/${selected.id}/users`, {
        method: "POST",
        token,
        body: JSON.stringify(values),
      });
      message.success("Merchant user added");
      setUserOpen(false);
      userForm.resetFields();
      loadMerchantDetails(selected);
    } catch (error: any) {
      message.error(error.message || "Could not add merchant user");
    }
  };

  const revokeKey = async (apiKeyId: string) => {
    if (!token || !selected?.id) return;
    try {
      await adminRequest(`/merchants/${selected.id}/api-keys/${apiKeyId}/revoke`, {method: "POST", token});
      message.success("API key revoked");
      loadMerchantDetails(selected);
    } catch (error: any) {
      message.error(error.message || "Could not revoke API key");
    }
  };

  const updateDispute = async (disputeId: string, status: number) => {
    if (!token || !selected?.id) return;
    try {
      await adminRequest(`/merchants/${selected.id}/disputes/${disputeId}`, {
        method: "PATCH",
        token,
        body: JSON.stringify({status}),
      });
      message.success("Dispute updated");
      loadMerchantDetails(selected);
    } catch (error: any) {
      message.error(error.message || "Could not update dispute");
    }
  };

  const printQr = (link: any) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`<html><body style="font-family:Arial;text-align:center;padding:40px"><h2>${link.title}</h2><p>${link.qrCodePayload || link.slug}</p><p>${window.location.origin}/payment-links/${link.slug}</p><script>window.print()</script></body></html>`);
    printWindow.document.close();
  };

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={ShopOutlined as any}
          title="Merchants"
          subtitle="Profile businesses, manage their payment links, API keys and transactions."
          actions={<Button size="large" icon={<PlusOutlined/>} onClick={() => setCreateOpen(true)}>New Merchant</Button>}
        />

        <Card className="mt-8 p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <SearchField className="md:max-w-xl md:flex-1" placeholder="Search merchants" value={searchParam} onChange={(event) => setSearchParam(event.target.value)}/>
            <Tooltip title="Off lists sub-merchants as rows of their own, which is what a search needs">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Switch checked={topLevelOnly} onChange={setTopLevelOnly} disabled={!!searchParam}/>
                Top-level only
              </div>
            </Tooltip>
          </div>
          <GenericTable
            loading={loading}
            data={merchants}
            pageInfo={pageInfo(merchants)}
            handlePageChange={loadMerchants}
            expandable={{
              rowExpandable: (row: any) => (row.subMerchantCount || 0) > 0,
              onExpand: (expanded: boolean, row: any) => {
                if (expanded) loadSubMerchants(row.id);
              },
              expandedRowRender: (row: any) => (
                <Table
                  rowKey="key"
                  size="small"
                  pagination={false}
                  loading={subMerchantsLoadingFor === row.id}
                  dataSource={subMerchants[row.id] || []}
                  columns={subMerchantColumns}
                />
              ),
            }}
            columns={[
              {
                title: "Business",
                key: "businessName",
                render: (_: any, row: any) => (
                  <div>
                    <div className="font-medium">{row.businessName}</div>
                    {row.platformBusinessName && (
                      <div className="text-xs" style={{color: COLORS.brand}}>under {row.platformBusinessName}</div>
                    )}
                  </div>
                ),
              },
              {title: "Email", dataIndex: "businessEmail", key: "businessEmail"},
              {title: "Phone", dataIndex: "businessPhone", key: "businessPhone"},
              {
                title: "Sub-merchants",
                dataIndex: "subMerchantCount",
                key: "subMerchantCount",
                render: (value: any) => value > 0 ? <Tag color="blue">{value}</Tag> : <span className="text-gray-400">—</span>,
              },
              {title: "Environment", dataIndex: "currentEnvironment", key: "currentEnvironment", render: (value: any) => <Tag>{String(value)}</Tag>},
              {title: "Status", dataIndex: "status", key: "status", render: (value: any) => <Tag color={String(value) === "Active" || value === 2 ? "green" : "default"}>{statusText(value)}</Tag>},
              {title: "Action", key: "action", render: (_: any, row: any) => <Button onClick={() => loadMerchantDetails(row)}>Open</Button>},
            ]}
          />
        </Card>

        <Modal open={createOpen} title="Profile merchant" onCancel={() => setCreateOpen(false)} onOk={() => form.submit()} width={760}>
          <Form form={form} layout="vertical" onFinish={createMerchant}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Form.Item label="Business name" name="businessName" rules={[{required: true}]}><Input/></Form.Item>
              <Form.Item label="Business email" name="businessEmail" rules={[{required: true, type: "email"}]}><Input/></Form.Item>
              <Form.Item label="Business phone" name="businessPhone"><Input/></Form.Item>
              <Form.Item label="Contact name" name="contactName"><Input/></Form.Item>
              <Form.Item label="Owner first name" name="ownerFirstName"><Input/></Form.Item>
              <Form.Item label="Owner last name" name="ownerLastName"><Input/></Form.Item>
              <Form.Item label="Owner email" name="ownerEmail" rules={[{type: "email"}]}><Input/></Form.Item>
              <Form.Item label="Temporary password" name="password"><Input.Password/></Form.Item>
              <Form.Item label="Settlement account name" name="settlementAccountName"><Input/></Form.Item>
              <Form.Item label="Settlement account number" name="settlementAccountNumber"><Input/></Form.Item>
              <Form.Item label="Settlement bank" name="settlementBankCode">
                <Select
                  showSearch
                  allowClear
                  loading={banksLoading}
                  placeholder="Select bank"
                  options={settlementBankOptions}
                  onChange={selectSettlementBank}
                  filterOption={(input, option) =>
                    String(option?.label || "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item name="settlementBankName" hidden><Input/></Form.Item>
            </div>
          </Form>
        </Modal>

        {/* antd v6 renamed Drawer's `width` to `size`, which still takes a pixel number. */}
        <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected?.businessName} size={980}>
          {selected && (
            <>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Email">{selected.businessEmail}</Descriptions.Item>
                <Descriptions.Item label="Phone">{selected.businessPhone || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Status">{statusText(selected.status)}</Descriptions.Item>
                <Descriptions.Item label="Environment">{String(selected.currentEnvironment)}</Descriptions.Item>
                <Descriptions.Item label="Platform">
                  {selected.platformMerchantId
                    ? (selected.platformBusinessName || "Onboarded by a platform partner")
                    : "None — top-level merchant"}
                </Descriptions.Item>
                <Descriptions.Item label="Sub-merchants">{selected.subMerchantCount || 0}</Descriptions.Item>
                <Descriptions.Item label="Platform reference">{selected.externalReference || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Platform fee">
                  {`${money(selected.platformFeeFlatAmount)} + ${bps(selected.platformFeePercentageBps)}`}
                  {selected.platformFeeCap ? ` · capped at ${money(selected.platformFeeCap)}` : ""}
                </Descriptions.Item>
              </Descriptions>
              <Space className="mt-4" wrap>
                <span>Switch environment</span>
                <Select
                  style={{width: 140}}
                  value={typeof selected.currentEnvironment === "number" ? selected.currentEnvironment : String(selected.currentEnvironment).toLowerCase() === "live" ? 2 : 1}
                  onChange={updateEnvironment}
                  options={[{label: "Test", value: 1}, {label: "Live", value: 2}]}
                />
                <Button onClick={() => {
                  feeForm.setFieldsValue({
                    flatAmount: selected.platformFeeFlatAmount || 0,
                    percentageBps: selected.platformFeePercentageBps || 0,
                    cap: selected.platformFeeCap,
                    waiveFlatBelow: selected.platformFeeWaiveFlatBelow,
                  });
                  setFeeOpen(true);
                }}>
                  Platform fee
                </Button>
              </Space>

              <Tabs className="mt-5" items={[
                {key: "links", label: "Payment Links", children: <>
                  <Button className="mb-3" icon={<PlusOutlined/>} onClick={() => setLinkOpen(true)}>New Link</Button>
                  <GenericTable data={links} pageInfo={pageInfo(links)} handlePageChange={() => loadMerchantDetails(selected)} columns={[
                    {title: "Title", dataIndex: "title", key: "title"},
                    {title: "Amount", key: "amount", render: (_: any, row: any) => row.allowCustomAmount ? "Custom" : money(row.fixedAmount, row.currency)},
                    {title: "QR Payload", dataIndex: "qrCodePayload", key: "qrCodePayload"},
                    {title: "Action", key: "action", render: (_: any, row: any) => <Button icon={<PrinterOutlined/>} onClick={() => printQr(row)}>Print</Button>},
                  ]}/>
                </>},
                {key: "transactions", label: "Transactions", children: <GenericTable data={transactions} pageInfo={pageInfo(transactions)} handlePageChange={() => loadMerchantDetails(selected)} columns={[
                  {title: "Payment Link", dataIndex: "paymentLinkTitle", key: "paymentLinkTitle"},
                  {title: "Payer", dataIndex: "payerEmail", key: "payerEmail"},
                  {title: "Amount", key: "amount", render: (_: any, row: any) => money(row.amount, row.currency)},
                  {title: "Status", dataIndex: "status", key: "status", render: (value: any) => <Tag>{String(value)}</Tag>},
                  {title: "Created", dataIndex: "createdAt", key: "createdAt", render: (value: string) => value ? new Date(value).toLocaleString() : ""},
                ]}/>},
                {key: "keys", label: "API Keys", children: <>
                  <Space className="mb-3">
                    <Button icon={<KeyOutlined/>} onClick={() => setKeyOpen(true)}>Generate Key</Button>
                    {createdKey && <Input readOnly value={createdKey} style={{width: 420}}/>}
                  </Space>
                  <GenericTable data={apiKeys} pageInfo={pageInfo(apiKeys)} handlePageChange={() => loadMerchantDetails(selected)} columns={[
                    {title: "Name", dataIndex: "name", key: "name"},
                    {title: "Prefix", dataIndex: "keyPrefix", key: "keyPrefix"},
                    {title: "Environment", dataIndex: "environment", key: "environment"},
                    {title: "Active", dataIndex: "isActive", key: "isActive", render: (value: boolean) => <Tag color={value ? "green" : "red"}>{value ? "Active" : "Revoked"}</Tag>},
                    {title: "Action", key: "action", render: (_: any, row: any) => <Button danger disabled={!row.isActive} onClick={() => revokeKey(row.id)}>Revoke</Button>},
                  ]}/>
                </>},
                {key: "users", label: "Users", children: <>
                  <Button className="mb-3" icon={<PlusOutlined/>} onClick={() => setUserOpen(true)}>Add User</Button>
                  <GenericTable data={users} pageInfo={pageInfo(users)} handlePageChange={() => loadMerchantDetails(selected)} columns={[
                    {title: "Email", dataIndex: "email", key: "email"},
                    {title: "Name", key: "name", render: (_: any, row: any) => `${row.firstName || ""} ${row.lastName || ""}`.trim()},
                    {title: "Role", dataIndex: "role", key: "role"},
                    {title: "Active", dataIndex: "isActive", key: "isActive", render: (value: boolean) => <Tag>{value ? "Yes" : "No"}</Tag>},
                  ]}/>
                </>},
                {key: "disputes", label: "Disputes", children: <GenericTable data={disputes} pageInfo={pageInfo(disputes)} handlePageChange={() => loadMerchantDetails(selected)} columns={[
                  {title: "Payment Link", dataIndex: "paymentLinkTitle", key: "paymentLinkTitle"},
                  {title: "Payer", dataIndex: "payerEmail", key: "payerEmail"},
                  {title: "Amount", key: "amount", render: (_: any, row: any) => money(row.amount, row.currency)},
                  {title: "Reason", dataIndex: "reason", key: "reason"},
                  {title: "Status", dataIndex: "status", key: "status", render: (value: any) => <Tag>{String(value)}</Tag>},
                  {title: "Action", key: "action", render: (_: any, row: any) => <Space><Button onClick={() => updateDispute(row.id, 3)}>Resolve</Button><Button danger onClick={() => updateDispute(row.id, 4)}>Reject</Button></Space>},
                ]}/>},
                {key: "sub-merchants", label: `Sub-merchants${selected.subMerchantCount ? ` (${selected.subMerchantCount})` : ""}`, children: (
                  <GenericTable
                    loading={subMerchantsLoadingFor === selected.id}
                    data={subMerchants[selected.id] || []}
                    pageInfo={pageInfo(subMerchants[selected.id] || [])}
                    handlePageChange={() => loadSubMerchants(selected.id)}
                    columns={subMerchantColumns}
                  />
                )},
              ]}/>
            </>
          )}
        </Drawer>

        <Modal open={linkOpen} title="Create merchant payment link" onCancel={() => setLinkOpen(false)} onOk={() => linkForm.submit()}>
          <Form form={linkForm} layout="vertical" onFinish={createPaymentLink} initialValues={{type: 1, allowCustomAmount: false}}>
            <Form.Item label="Title" name="title" rules={[{required: true}]}><Input/></Form.Item>
            <Form.Item label="Description" name="description"><Input.TextArea rows={3}/></Form.Item>
            <Form.Item label="Type" name="type"><Select options={[{label: "General", value: 1}, {label: "Invoice", value: 5}, {label: "Donation", value: 4}]}/></Form.Item>
            <Form.Item label="Fixed amount" name="fixedAmount"><InputNumber min={1} className="w-full"/></Form.Item>
            <Form.Item label="Allow custom amount" name="allowCustomAmount"><Select options={[{label: "No", value: false}, {label: "Yes", value: true}]}/></Form.Item>
          </Form>
        </Modal>

        {/*
          Deliberately an admin control and not a merchant one: a platform able to set its own
          rate could raise what its sub-merchants' payers are charged without anyone agreeing.
        */}
        <Modal open={feeOpen} title="Platform fee" onCancel={() => setFeeOpen(false)} onOk={() => feeForm.submit()}>
          <p className="text-gray-600">
            What {selected?.businessName || "this merchant"} earns, as a platform, on payments to
            the sub-merchants it onboarded. Zero means it takes nothing.
          </p>
          <Form form={feeForm} layout="vertical" onFinish={setPlatformFee}>
            <Form.Item label="Flat amount (₦)" name="flatAmount"><InputNumber min={0} className="w-full"/></Form.Item>
            <Form.Item label="Percentage (basis points — 150 is 1.5%)" name="percentageBps">
              <InputNumber min={0} max={10000} className="w-full"/>
            </Form.Item>
            <Form.Item label="Cap per payment (₦) — blank is uncapped" name="cap">
              <InputNumber min={0} className="w-full"/>
            </Form.Item>
            <Form.Item label="Waive the flat part below (₦)" name="waiveFlatBelow">
              <InputNumber min={0} className="w-full"/>
            </Form.Item>
          </Form>
        </Modal>

        <Modal open={keyOpen} title="Generate API key" onCancel={() => setKeyOpen(false)} onOk={() => keyForm.submit()}>
          <Form form={keyForm} layout="vertical" onFinish={createApiKey} initialValues={{environment: 1}}>
            <Form.Item label="Name" name="name" rules={[{required: true}]}><Input placeholder="Cashier app"/></Form.Item>
            <Form.Item label="Environment" name="environment"><Select options={[{label: "Test", value: 1}, {label: "Live", value: 2}]}/></Form.Item>
          </Form>
        </Modal>

        <Modal open={userOpen} title="Add merchant user" onCancel={() => setUserOpen(false)} onOk={() => userForm.submit()}>
          <Form form={userForm} layout="vertical" onFinish={createUser} initialValues={{role: 2}}>
            <Form.Item label="Email" name="email" rules={[{required: true, type: "email"}]}><Input/></Form.Item>
            <Form.Item label="First name" name="firstName"><Input/></Form.Item>
            <Form.Item label="Last name" name="lastName"><Input/></Form.Item>
            <Form.Item label="Temporary password" name="password"><Input.Password/></Form.Item>
            <Form.Item label="Role" name="role"><Select options={[{label: "Admin", value: 2}, {label: "Cashier", value: 3}, {label: "Viewer", value: 4}]}/></Form.Item>
          </Form>
        </Modal>
      </main>
    </AdminLayout>
  );
}

export default Merchants;
